import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useBranding } from "@/lib/branding";
import { useAuth } from "@/lib/auth";
import PayPalModal from "@/components/PayPalModal";
import CashAppModal from "@/components/CashAppModal";
import {
  ArrowRight,
  BookOpen,
  GraduationCap,
  Sparkles,
  UserCheck,
  Check,
  Compass,
  Wallet,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const PLAN_1040 = { id: "student", name: "Master the 1040", amount: 175 };

/**
 * Dedicated landing page for the Form 1040 course — targets aspiring
 * tax preparers who want to enter the industry from scratch. Sister page
 * to /1120-refresher and /white-label — same rhythm, different palette.
 */
export default function Course1040Landing() {
  const { branding } = useBranding();
  const { user } = useAuth();
  const [paypalOpen, setPaypalOpen] = useState(false);
  const [cashOpen, setCashOpen] = useState(false);
  const [paypalHandle, setPaypalHandle] = useState("Dynamictax80");
  const [cashTag, setCashTag] = useState("$DimekaH");

  useEffect(() => {
    axios.get(`${API}/api/payments/plans`).then((r) => {
      setPaypalHandle(r.data?.paypal_handle || "Dynamictax80");
      setCashTag(r.data?.cash_app_tag || "$DimekaH");
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="course-1040-page">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" />
            ) : (
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">
                {branding.brand_name?.[0]}
              </div>
            )}
            <div className="font-serif text-2xl">{branding.brand_name}</div>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/" className="hidden sm:inline underline text-muted-foreground hover:text-foreground">Home</Link>
            <Link to="/1120-refresher" className="hidden sm:inline underline text-muted-foreground hover:text-foreground">1120 Refresher</Link>
            <Link to="/afsp-bundle" className="hidden sm:inline underline text-accent hover:text-accent/80 font-medium">AFSP Prep</Link>
            <Link to="/pricing" className="hidden sm:inline underline text-muted-foreground hover:text-foreground">Pricing</Link>
            <Link to="/white-label" className="hidden sm:inline underline text-primary hover:text-primary/80 font-medium">White-Label</Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "radial-gradient(ellipse at top, rgba(20,120,80,0.28) 0%, rgba(15,23,42,0.98) 55%, #0a0f1f 100%)",
        }}
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-20 lg:py-24 text-center">
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 text-xs font-medium">
            <GraduationCap size={13} className="text-emerald-300" /> Start your tax career
          </div>
          <h1
            className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] text-white mb-6"
            data-testid="course-1040-hero-title"
          >
            Master the 1040.
            <br />
            <span className="italic bg-clip-text text-transparent bg-gradient-to-r from-emerald-200 via-white to-emerald-200">
              File your first return by season.
            </span>
          </h1>
          <p className="text-lg md:text-xl text-white/70 mb-3 tracking-wide">
            13 chapters. Zero background required. Real preparer skills.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-4 text-xs text-white/80">
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/20">13 chapters</span>
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/20">4 practice returns</span>
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/20">25-question final exam</span>
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/20">AI tax teacher · 24/7</span>
          </div>

          <div className="mt-12 inline-flex items-baseline gap-3">
            <span className="font-serif text-6xl md:text-7xl text-white">$175</span>
            <span className="text-sm text-white/60">/ one-time</span>
          </div>
          <div className="mt-2 text-xs text-emerald-200 font-medium tracking-wide">
            LIFETIME ACCESS · CERTIFICATE INCLUDED
          </div>

          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Button
              size="lg"
              className="bg-[#0070ba] hover:bg-[#005ea6] text-white"
              onClick={() => setPaypalOpen(true)}
              data-testid="course-1040-cta-paypal"
            >
              <Wallet size={16} className="mr-2" /> Pay $175 with PayPal
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/40 text-white hover:bg-white/10"
              onClick={() => setCashOpen(true)}
              data-testid="course-1040-cta-cashapp"
            >
              <Wallet size={16} className="mr-2" /> Pay with Cash App
            </Button>
            <Button asChild size="lg" variant="ghost" className="text-white hover:bg-white/10" data-testid="course-1040-cta-preview">
              <Link to="/course/1040">Preview 3 chapters free</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="border-t border-border bg-secondary/40">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-center mb-10">
            <div className="label-caps mb-3 text-primary">Zero background required</div>
            <h2 className="font-serif text-4xl md:text-5xl">
              This is for you if you're…
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {AUDIENCE.map((a, i) => (
              <div key={i} className="border border-border rounded-lg p-6 bg-card" data-testid={`course-1040-audience-${i}`}>
                <div className="h-10 w-10 rounded-md bg-primary/10 text-primary grid place-items-center mb-3">
                  {a.icon}
                </div>
                <h3 className="font-serif text-lg mb-2">{a.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section className="border-t border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-center mb-12">
            <div className="label-caps mb-3 text-primary">The 13 chapters</div>
            <h2 className="font-serif text-4xl md:text-5xl">
              From filing status to your <span className="italic text-primary">first real return.</span>
            </h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
              You'll finish knowing every line of the 1040 by heart — filing status, dependents, W-2 income, Schedule C for gig workers, credits, deductions, and how to actually deliver a return to a paying client.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {CHAPTERS.map((title, i) => (
              <div key={i} className="flex items-start gap-3" data-testid={`course-1040-ch-${i + 1}`}>
                <div className="shrink-0 h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-serif grid place-items-center mt-0.5">
                  {i + 1}
                </div>
                <div className="text-sm leading-relaxed pt-0.5">{title}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="border-t border-border bg-secondary/30">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-center mb-10">
            <div className="label-caps mb-3 text-primary">What $175 gets you</div>
            <h2 className="font-serif text-4xl md:text-5xl">Everything you need to bill your first client.</h2>
          </div>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {INCLUDED.map((item, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`course-1040-inc-${i}`}>
                <div className="mt-0.5 h-5 w-5 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0">
                  <Check size={13} />
                </div>
                <span className="text-sm leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* BUNDLE UPSELL */}
      <section className="border-t border-border">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16">
          <div className="border border-primary rounded-lg p-8 bg-primary/5 flex flex-col md:flex-row items-start md:items-center gap-6" data-testid="course-1040-bundle-upsell">
            <div className="shrink-0 h-14 w-14 rounded-md bg-primary text-primary-foreground grid place-items-center">
              <Sparkles size={28} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-primary font-medium mb-1">Level up</div>
              <h3 className="font-serif text-2xl mb-2">Bundle with 1120 for $425 total</h3>
              <p className="text-sm text-muted-foreground">
                Add the Form 1120 Refresher to become a full-spectrum preparer (individual + corporate). Save $0 on the pair versus buying separately, but you launch season with two skill tiers instead of one.
              </p>
            </div>
            <Button asChild data-testid="course-1040-bundle-cta">
              <Link to="/pricing#bundle">See bundle <ArrowRight size={14} className="ml-2" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-center mb-10">
            <div className="label-caps mb-3 text-primary">Frequently asked</div>
            <h2 className="font-serif text-4xl md:text-5xl">
              The short version.
            </h2>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="course-1040-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`course-1040-faq-item-${i}`}>
                <AccordionTrigger className="text-left font-serif text-lg">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-border">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-24 text-center">
          <h2 className="font-serif text-4xl md:text-6xl mb-4">
            Your <span className="italic text-primary">tax career</span> starts today.
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
            Independent preparers bill <strong className="text-foreground">$150–$400</strong> per return. Get through the 13 chapters, pass the exam, and you're ready to earn.
          </p>
          <div className="inline-flex items-baseline gap-3 mb-8">
            <span className="font-serif text-5xl">$175</span>
            <span className="text-sm text-muted-foreground">· one-time · lifetime access</span>
          </div>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button size="lg" className="bg-[#0070ba] hover:bg-[#005ea6] text-white" onClick={() => setPaypalOpen(true)} data-testid="course-1040-final-cta-paypal">
              <Wallet size={16} className="mr-2" /> Pay $175 with PayPal
            </Button>
            <Button size="lg" variant="outline" onClick={() => setCashOpen(true)} data-testid="course-1040-final-cta-cashapp">
              <Wallet size={16} className="mr-2" /> Pay with Cash App
            </Button>
            <Button asChild size="lg" variant="ghost" data-testid="course-1040-final-cta-preview">
              <Link to="/course/1040">Preview 3 chapters free</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-8 text-sm text-muted-foreground flex flex-col sm:flex-row items-center justify-between gap-3">
          <div>Powered by <span className="text-foreground">{branding.brand_name}</span></div>
          <div className="flex gap-4">
            <Link to="/" className="underline hover:text-foreground">Home</Link>
            <Link to="/1120-refresher" className="underline hover:text-foreground">1120 Refresher</Link>
            <Link to="/pricing" className="underline hover:text-foreground">Pricing</Link>
          </div>
        </div>
      </footer>

      <PayPalModal
        open={paypalOpen}
        onClose={() => setPaypalOpen(false)}
        plan={PLAN_1040}
        paypalHandle={paypalHandle}
        user={user}
      />
      <CashAppModal
        open={cashOpen}
        onClose={() => setCashOpen(false)}
        plan={PLAN_1040}
        cashTag={cashTag}
        user={user}
      />
    </div>
  );
}

const AUDIENCE = [
  {
    icon: <Compass size={22} />,
    title: "New to taxes",
    body: "You've never prepared a return but want to enter the industry — as a side hustle, career switch, or a real business you can grow.",
  },
  {
    icon: <UserCheck size={22} />,
    title: "Filing for family already",
    body: "You've done a return or two for family and want to turn it into a paid, professional service you can charge for.",
  },
  {
    icon: <BookOpen size={22} />,
    title: "Preparing for the AFSP or IRS PTIN",
    body: "You want a real refresher on 2025 law before applying for your PTIN or completing an Annual Filing Season Program course.",
  },
];

const CHAPTERS = [
  "Tax Fundamentals & Filing Status",
  "Mastering Form 1040",
  "Income Types",
  "Adjustments to Income",
  "Credits & Deductions",
  "Schedule C Mastery: Small Business Taxes",
  "Schedule E: Rental Property Income",
  "1099s, K-1s, and Other Complex Income",
  "Capital Gains, Losses & Investments",
  "Retirement & Pension Taxation",
  "IRS Circular 230 & Preparer Ethics",
  "Due Diligence & IRS Forms",
  "Live Practice Returns",
];

const INCLUDED = [
  "13 in-depth chapters written in plain English (no jargon walls)",
  "AI tax teacher trained on the full course — ask any 1040 question 24/7",
  "4 fully guided practice returns modeled on real clients (W-2 worker, gig, HOH with kids, retiree)",
  "Interactive refund calculator with 2025 brackets baked in",
  "Mid-chapter checkpoint quizzes to lock in each concept",
  "25-question final exam (80% to pass)",
  "Downloadable, dated certificate you can send clients or add to your website",
  "Lifetime access — every future 2026/2027 update included",
  "Preview Chapters 1–3 for free before you enroll",
  "Certificate that qualifies you to market yourself as a trained preparer",
];

const FAQS = [
  {
    q: "Do I need a tax background to start?",
    a: "Zero. Chapter 1 assumes you've never opened a 1040 before. By Chapter 3 you'll know income types cold, and by Chapter 13 you'll have prepared 4 realistic returns from scratch.",
  },
  {
    q: "Will I get a certificate?",
    a: "Yes — pass the 25-question final exam with 80% or higher and you get a downloadable PDF certificate dated the day you complete it. Perfect for your website, LinkedIn, or client-facing marketing.",
  },
  {
    q: "Are the 2025 tax law changes included?",
    a: "Yes. The whole course was refreshed to Tax Year 2025 in September — standard deduction, EITC limits, mileage rate, retirement contribution caps, all of it. When the IRS publishes 2026 numbers, we update the course and you keep access.",
  },
  {
    q: "Can I make money after finishing this?",
    a: "Absolutely. Independent preparers bill $150–$400 per 1040 return. One client covers this course. Twenty clients (a slow first season) is $3,000–$8,000 in your pocket, with the skills growing every year.",
  },
  {
    q: "What if I want to add corporate returns too?",
    a: "Grab the 1040 + 1120 bundle at $425 — you become a full-spectrum preparer who can serve individuals AND small businesses, which is the real earning tier.",
  },
  {
    q: "What's the refund policy?",
    a: "Because course content unlocks the moment you pay, all sales are final once you begin Chapter 1. Before opening any chapter, you can request a 100% refund within 7 days — email support and we'll process it.",
  },
];
